import React, { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Search, Loader2, ArrowRightCircle } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useNavigate } from 'react-router-dom';
import { useSymbolSearch } from '@/hooks/market-data/useSymbolSearch';

interface Stock {
  symbol: string;
  name: string;
  exchange?: string;
  assetType?: string;
  type?: string;
  country?: string;
}

interface StockSearchBarProps {
  onSymbolSelect?: (symbol: string) => void;
  selectedSymbol?: string;
}

export const StockSearchBar: React.FC<StockSearchBarProps> = ({ onSymbolSelect, selectedSymbol }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Stock[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  
  // Use the symbolSearch hook which provides a unified search across providers
  const { searchSymbols, isLoading: hookLoading } = useSymbolSearch();

  // Initialize search term based on selected symbol
  useEffect(() => {
    if (selectedSymbol) {
      setSearchTerm(selectedSymbol);
    } else {
      const storedSearchTerm = localStorage.getItem('stockSearchTerm');
      if (storedSearchTerm) {
        setSearchTerm(storedSearchTerm);
        searchStocks(storedSearchTerm);
      }
    }
  }, [selectedSymbol]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  const searchStocks = async (term: string) => {
    if (!term) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      // Use our improved useSymbolSearch hook - this is now much more robust
      const hookResults = await searchSymbols(term);
      if (hookResults && hookResults.length > 0) {
        // Convert to Stock interface format
        const formattedResults = hookResults.map(item => ({
          symbol: item.symbol,
          name: item.name,
          exchange: item.exchange || '',
          assetType: item.type || 'Equity',
          type: item.type || 'Equity'
        }));
        setResults(formattedResults);
      } else {
        // If the term looks like a ticker symbol, create a direct match
        if (term.length <= 5 && /^[A-Za-z0-9.]+$/.test(term)) {
          setResults([{
            symbol: term.toUpperCase(),
            name: term.toUpperCase(),
            exchange: 'UNKNOWN',
            assetType: 'Equity',
            type: 'Equity'
          }]);
        } else {
          setResults([]);
        }
      }
    } catch (error) {
      console.error("Could not complete search", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm) {
      localStorage.setItem('stockSearchTerm', searchTerm);
      
      // Debounce the search to avoid too many API calls
      const timerId = setTimeout(() => {
        searchStocks(searchTerm);
      }, 300);
      
      return () => clearTimeout(timerId);
    } else {
      setResults([]);
    }
  }, [searchTerm]);

  const handleSelect = (symbol: string) => {
    setSearchTerm(symbol);
    setResults([]);
    setOpen(false);
    
    // Always call onSymbolSelect if provided
    if (onSymbolSelect) {
      onSymbolSelect(symbol);
    } else {
      // Only navigate if no callback is provided
      navigate(`/analysis/${symbol}`);
    }
  };

  // Handle direct form submission (pressing Enter key)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // If valid search term exists, select it
    if (searchTerm && searchTerm.trim().length > 0) {
      handleSelect(searchTerm.toUpperCase());
    }
  };

  // Check if the search term looks like a valid ticker symbol
  const isValidTickerSymbol = searchTerm && searchTerm.length <= 5 && /^[A-Za-z0-9.]+$/.test(searchTerm);

  return (
    <form onSubmit={handleSubmit}>
      <div className="relative">
        <Input
          type="text"
          placeholder="Search stocks..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setOpen(true);
          }}
          className="pl-10"
          ref={searchInputRef}
          onFocus={() => setOpen(true)}
        />
        {loading ? (
          <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 animate-spin" />
        ) : (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
        )}
      </div>
    
      {open && searchTerm && (
        <div className="relative mt-1">
          <div className="absolute z-20 bg-white border border-gray-200 rounded-md shadow-lg" style={{ minWidth: '100%', maxWidth: 'max-content' }}>
            <Command className="rounded-lg">
              <CommandList className="overflow-auto" style={{ maxHeight: '40vh' }}>
                {loading ? (
                  <div className="py-6 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
                  </div>
                ) : results.length === 0 ? (
                  <CommandEmpty>
                    {isValidTickerSymbol ? (
                      <div className="flex items-center justify-between p-2 text-sm">
                        <div>
                          <span className="font-medium">{searchTerm.toUpperCase()}</span>
                          <br />
                          <span className="text-gray-500">Press Enter to view this symbol</span>
                        </div>
                        <ArrowRightCircle className="h-4 w-4 text-blue-500" />
                      </div>
                    ) : (
                      <div>No stocks found. Try a different search term.</div>
                    )}
                  </CommandEmpty>
                ) : (
                  <CommandGroup heading="Stocks">
                    {results.map((stock) => (
                      <CommandItem
                        key={stock.symbol}
                        value={stock.symbol}
                        onSelect={() => handleSelect(stock.symbol)}
                        className="cursor-pointer"
                      >
                        <div className="flex items-start gap-2 w-full">
                          <div className="font-medium min-w-16 flex-shrink-0">{stock.symbol}</div>
                          <div className="text-sm text-gray-500 whitespace-normal">{stock.name}</div>
                          {stock.exchange && (
                            <span className="text-xs text-gray-400 flex-shrink-0 ml-auto">{stock.exchange}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </div>
        </div>
      )}
    </form>
  );
};

export default StockSearchBar;